import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["path", "columns", "childKey", "handleToggleSearchVisibility", "allowBulkEdit", "onStartEdit", "afterSaveCell", "handleNewItemAdded", "addButtonTooltip", "title", "hideAddButton", "hideFilterButton", "hideTableWhenEmpty", "pageSize", "insertNewItemsAtTheEnd", "sliderConfigKeyPath", "sliderAction"];
//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import * as Config from "eai-configurator-ui/components/configuration/utils/ConfigurationRegistry";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import ListIcon from "@material-ui/icons/List";
import SearchIcon from "@material-ui/icons/Search";
import BootstrapTable from "react-bootstrap-table-next";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.css";
import filterFactory from "react-bootstrap-table2-filter";
import cellEditFactory from "react-bootstrap-table2-editor";
import ConfigurationBulkEditDialog from "eai-configurator-ui/components/configuration/bulk_edit/ConfigurationBulkEditDialog";
import paginationFactory, { PaginationListStandalone, PaginationProvider, PaginationTotalStandalone } from "react-bootstrap-table2-paginator";
import { convertConfigDataToTableData } from "eai-configurator-ui/components/configuration/table/ConfigurationTableUtils";
import ConfigurationSwitch from "eai-configurator-ui/components/configuration/base/ConfigurationSwitch";
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      width: "100%"
    },
    paper: {
      width: "100%",
      paddingTop: theme.spacing(0.5)
    },
    tableIconButton: {},
    tableHeader: {
      backgroundColor: "#f6f6f6"
    }
  };
});
var ConfigurationTable = function ConfigurationTable(_ref) {
  var path = _ref.path,
    columns = _ref.columns,
    childKey = _ref.childKey,
    handleToggleSearchVisibility = _ref.handleToggleSearchVisibility,
    allowBulkEdit = _ref.allowBulkEdit,
    onStartEdit = _ref.onStartEdit,
    afterSaveCell = _ref.afterSaveCell,
    handleNewItemAdded = _ref.handleNewItemAdded,
    addButtonTooltip = _ref.addButtonTooltip,
    title = _ref.title,
    hideAddButton = _ref.hideAddButton,
    hideFilterButton = _ref.hideFilterButton,
    hideTableWhenEmpty = _ref.hideTableWhenEmpty,
    pageSize = _ref.pageSize,
    insertNewItemsAtTheEnd = _ref.insertNewItemsAtTheEnd,
    sliderConfigKeyPath = _ref.sliderConfigKeyPath,
    sliderAction = _ref.sliderAction,
    tableProps = _objectWithoutProperties(_ref, _excluded);
  var classes = useStyles();
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    forceUpdateCounter = _React$useState2[0],
    setForceUpdateCounter = _React$useState2[1];
  var forceUpdate = function forceUpdate() {
    setForceUpdateCounter(forceUpdateCounter + 1);
  };
  //const [sliderActionChecked, setSliderActionChecked] = React.useState(false);
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    bulkEditOpen = _React$useState4[0],
    setBulkEditOpen = _React$useState4[1];
  var _React$useState5 = React.useState(1),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    pageIndex = _React$useState6[0],
    setPageIndex = _React$useState6[1];
  var data = convertConfigDataToTableData(path, columns);
  var handleCellEdited = function handleCellEdited(oldValue, newValue, row, column) {
    Config.setDataValue("".concat(path, ":").concat(childKey, "[").concat(row.dataIndex, "]").concat(column.dataField), newValue);
    if (afterSaveCell) {
      afterSaveCell(oldValue, newValue, row, column);
    }
  };

  /**
   * Called upon clicking the add button.
   * A new item is created out of the default configuration definition and
   * it's added as a child of the parent configuration.
   */
  var createNewItem = function createNewItem(path, addAsFirstItem) {
    var newItem = Config.getDefault("".concat(path, ":").concat(childKey));

    // Clear all children - the table only supports a flat child configuration definition without children
    newItem.children.map(function (child) {
      return child.children = [];
    });
    var parent = Config.fromPath(path, false, true);
    Config.addChild(parent, newItem, addAsFirstItem);
    return newItem;
  };
  var handleAddItem = function handleAddItem() {
    var newItem = createNewItem(path, !Boolean(insertNewItemsAtTheEnd));
    if (handleNewItemAdded) {
      handleNewItemAdded(newItem);
    }

    // Force component rerender to pick up the newly added configuration item (which will appear as a new row on the table)
    forceUpdate();
  };
  var sliderChangeAction = function sliderChangeAction(checked) {
    if (sliderAction) {
      sliderAction(checked);
      forceUpdate();
    }
  };

  //-----------------------------------
  // Pagination
  //-----------------------------------

  var sizePerPage = pageSize ? pageSize : 10;
  var page = pageIndex;
  var lastPageIndex = data.length / sizePerPage;
  if (data.length % sizePerPage === 0) {
    lastPageIndex--;
  }
  if (lastPageIndex + 1 < page) {
    page = 1;
  }
  var paginationOptions = {
    custom: true,
    sizePerPage: sizePerPage,
    totalSize: data.length,
    hidePageListOnlyOnePage: true,
    page: page,
    onPageChange: setPageIndex
  };
  var showPaginationBelowTable = data.length >= sizePerPage;

  //-----------------------------------
  // Top component (caption)
  //-----------------------------------
  var caption = /*#__PURE__*/React.createElement(Box, {
    p: 1.5,
    display: "flex",
    style: {
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement(Box, {
    width: "100%"
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.title,
    variant: "h6",
    id: "tableTitle"
  }, title ? title : Config.getDefault(path).data.label)), allowBulkEdit && /*#__PURE__*/React.createElement(Box, {
    flexShrink: 1
  }, /*#__PURE__*/React.createElement(Tooltip, {
    title: "Bulk Edit"
  }, /*#__PURE__*/React.createElement(IconButton, {
    onClick: function onClick() {
      return setBulkEditOpen(true);
    }
  }, /*#__PURE__*/React.createElement(ListIcon, null)))), !hideFilterButton && /*#__PURE__*/React.createElement(Box, {
    flexShrink: 1
  }, /*#__PURE__*/React.createElement(Tooltip, {
    title: "Search/Filter"
  }, /*#__PURE__*/React.createElement(IconButton, {
    className: classes.tableIconButton,
    onClick: handleToggleSearchVisibility
  }, /*#__PURE__*/React.createElement(SearchIcon, null)))), !hideAddButton && /*#__PURE__*/React.createElement(Box, {
    flexShrink: 1
  }, /*#__PURE__*/React.createElement(Tooltip, {
    title: addButtonTooltip ? addButtonTooltip : "Add"
  }, /*#__PURE__*/React.createElement(IconButton, {
    className: classes.tableIconButton,
    onClick: handleAddItem
  }, /*#__PURE__*/React.createElement(AddIcon, null))), " "), sliderAction && sliderConfigKeyPath && /*#__PURE__*/React.createElement(Box, {
    flexShrink: 1
  }, /*#__PURE__*/React.createElement(ConfigurationSwitch, {
    path: sliderConfigKeyPath,
    handleChanged: sliderChangeAction,
    labelProps: {
      style: {
        whiteSpace: "nowrap"
      }
    }
  })));
  if (hideTableWhenEmpty && data.length === 0) {
    return null;
  }

  //-----------------------------------
  // Table
  //-----------------------------------
  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: "100%"
    }
  }, bulkEditOpen && /*#__PURE__*/React.createElement(ConfigurationBulkEditDialog, {
    path: path,
    childKey: childKey,
    columns: columns.filter(function (column) {
      return column.bulkEditable;
    }).map(function (column) {
      return Object({
        key: column.dataField
      });
    }),
    title: "Bulk Edit",
    open: bulkEditOpen,
    handleCancel: function handleCancel() {
      return setBulkEditOpen(false);
    }
  }), /*#__PURE__*/React.createElement(PaginationProvider, {
    pagination: paginationFactory(paginationOptions)
  }, function (_ref2) {
    var paginationProps = _ref2.paginationProps,
      paginationTableProps = _ref2.paginationTableProps;
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Paper, {
      className: classes.paper
    }, caption, /*#__PURE__*/React.createElement(BootstrapTable, Object.assign({
      headerClasses: classes.tableHeader,
      bootstrap4: true,
      keyField: "dataIndex",
      data: data,
      columns: columns,
      filter: filterFactory(),
      filterPosition: "top",
      cellEdit: cellEditFactory({
        mode: "click",
        blurToSave: true,
        afterSaveCell: handleCellEdited,
        onStartEdit: onStartEdit
      }),
      noDataIndication: "Table is Empty"
    }, paginationTableProps, tableProps))), showPaginationBelowTable && /*#__PURE__*/React.createElement(Box, {
      p: 0,
      display: "flex",
      style: {
        width: "100%"
      }
    }, /*#__PURE__*/React.createElement(Box, {
      width: "100%"
    }, /*#__PURE__*/React.createElement(PaginationTotalStandalone, paginationProps)), /*#__PURE__*/React.createElement(Box, {
      flexShrink: 1
    }, /*#__PURE__*/React.createElement(PaginationListStandalone, paginationProps))));
  }));
};
export default ConfigurationTable;